import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../hooks/useProjectStore';
import {
  fractureAPI, warningAPI,
  MonitorRun, MonitorRunData, WarnScenario,
} from '../services/api';

// ═══════════════════════════════════════════════
// 工况预警 — Nolte-Smith 双对数 + 实时曲线 + 事件流
// ═══════════════════════════════════════════════
const TH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.08)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  warn: '#D4770C', critical: '#C0392B', ok: '#1E8449',
  headerBg: '#FFFFFF', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  statBorder: '#ECEEF1',
  pressure: '#C2533A',  // 油压 砖红
  bhp: '#8E44AD',       // 井底压力 紫
  rate: '#2874A6',      // 排量 蓝
  conc: '#D4770C',      // 砂比 橙
  net: '#C0392B',       // 净压 深红
  fontUI: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

const VERDICT_COLOR: Record<string, string> = {
  safe: TH.ok, warn: TH.warn, alarm: TH.critical,
};
const VERDICT_LABEL: Record<string, string> = {
  safe: '正常', warn: '预警', alarm: '报警',
};
const SCENARIO_LABEL: Record<string, string> = {
  normal: '正常', screenout: '砂堵', frac_hit: '压窜', real: '实测',
};
const MODE_LABEL: Record<string, string> = {
  I: 'I 正常延伸', II: 'II 高度增长', IIIa: 'IIIa 砂堵前兆',
  IV: 'IV 砂堵', V: 'V 压窜/破坏',
};
const MODE_COLOR: Record<string, string> = {
  I: '#1E8449', II: '#2874A6', IIIa: '#D4770C', IV: '#C0392B', V: '#6A4C93',
};

// 进程缓存避免切回闪烁
const WP_RUNS: Record<number, MonitorRun[]> = {};
const WP_DATA: Record<number, MonitorRunData> = {};

type Design = { id: number; name: string; is_active: boolean; stage_count: number };
type Stage = { id: number; stage_number: number; top_perf: number; bottom_perf: number };

export default function WarningPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;

  const [designs, setDesigns] = useState<Design[]>([]);
  const [designId, setDesignId] = useState<number | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [stageNo, setStageNo] = useState<number | null>(null);
  const [scenario, setScenario] = useState<WarnScenario>('normal');
  const [duration, setDuration] = useState(30);

  const [runs, setRuns] = useState<MonitorRun[]>(projectId ? WP_RUNS[projectId] ?? [] : []);
  const [runId, setRunId] = useState<number | null>(null);
  const [data, setData] = useState<MonitorRunData | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!(projectId && WP_RUNS[projectId]));

  const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);

  const loadDesigns = async () => {
    if (!projectId) return;
    const { data } = await fractureAPI.listDesigns(projectId);
    const ds: Design[] = un(data);
    setDesigns(ds);
    const act = ds.find((d) => d.is_active) ?? ds[0];
    if (act) setDesignId(act.id);
  };

  const loadStages = async (did: number) => {
    const { data } = await fractureAPI.listStages(did);
    const ss: Stage[] = un(data);
    setStages(ss);
    if (ss.length && stageNo == null) setStageNo(ss[0].stage_number);
  };

  const loadRuns = async () => {
    if (!projectId) return;
    const { data } = await warningAPI.listRuns(projectId);
    // 实时分析页只管"合成监测";真实导入数据(source=imported)归到「监测会话」详情页
    const rs: MonitorRun[] = un(data).filter((r: MonitorRun) => r.source !== 'imported');
    setRuns(rs);
    WP_RUNS[projectId] = rs;
    if (rs.length && runId == null) setRunId(rs[0].id);
  };

  const loadData = async (id: number) => {
    if (WP_DATA[id]) {
      setData(WP_DATA[id]);
      return;
    }
    const { data } = await warningAPI.getRunData(id);
    setData(data);
    WP_DATA[id] = data;
  };

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    Promise.all([loadDesigns(), loadRuns()])
      .catch((e) => message.error(e?.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    if (designId) loadStages(designId).catch(() => {});
  }, [designId]);

  useEffect(() => {
    if (runId != null) loadData(runId).catch((e) => message.error(e?.message || '加载监测数据失败'));
    else setData(null);
  }, [runId]);

  const generate = async () => {
    if (!designId || stageNo == null) { message.warning('请选择方案和段号'); return; }
    setBusy(true);
    try {
      const { data: out } = await warningAPI.generateRun({
        design_id: designId, stage_number: stageNo, scenario, duration_min: duration,
      });
      message.success(`生成完成 · ${VERDICT_LABEL[out.verdict] || out.verdict} · ${out.event_count} 个事件`);
      // 清缓存重拉
      if (projectId) delete WP_RUNS[projectId];
      await loadRuns();
      setRunId(out.id);
    } catch (err: any) {
      const d = err?.response?.data;
      message.error(typeof d === 'string' ? d : d?.detail || '生成失败');
    } finally {
      setBusy(false);
    }
  };

  const removeRun = async (id: number) => {
    if (!confirm('删除这条监测会话?')) return;
    try {
      await warningAPI.deleteRun(id);
      delete WP_DATA[id];
      if (projectId) delete WP_RUNS[projectId];
      await loadRuns();
      if (runId === id) setRunId(runs.find((r) => r.id !== id)?.id ?? null);
      message.success('已删除');
    } catch {
      message.error('删除失败');
    }
  };

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      {/* ── Header ── */}
      <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, background: TH.headerBg, borderBottom: `1px solid ${TH.headerBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>工况预警 · Nolte-Smith 双对数分析</h2>
          <select value={designId ?? ''} onChange={(e) => setDesignId(Number(e.target.value) || null)}
            style={selStyle}>
            {designs.length === 0 && <option>无方案</option>}
            {designs.map((d) => <option key={d.id} value={d.id}>{d.is_active ? '★ ' : ''}{d.name}</option>)}
          </select>
          <select value={stageNo ?? ''} onChange={(e) => setStageNo(Number(e.target.value))} style={selStyle}>
            {stages.length === 0 && <option>无段</option>}
            {stages.map((s) => <option key={s.id} value={s.stage_number}>第 {s.stage_number} 段 ({s.top_perf}-{s.bottom_perf}m)</option>)}
          </select>
          <select value={scenario} onChange={(e) => setScenario(e.target.value as WarnScenario)} style={selStyle}>
            <option value="normal">场景: 正常</option>
            <option value="screenout">场景: 砂堵</option>
            <option value="frac_hit">场景: 压窜</option>
          </select>
          <label style={{ fontSize: 11, color: TH.textSec }}>
            时长 <input type="number" value={duration} min={5} max={120}
              onChange={(e) => setDuration(Number(e.target.value) || 30)}
              style={{ width: 50, fontSize: 11, padding: '2px 4px', borderRadius: 3, border: `1px solid ${TH.headerBorder}` }} /> min
          </label>
          <button onClick={generate} disabled={busy} style={{
            padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer', border: 'none',
            background: busy ? '#A89AC0' : TH.accent, color: '#fff',
          }}>{busy ? '生成中…' : '⚡ 生成监测数据'}</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {runs.length > 0 && (
            <select value={runId ?? ''} onChange={(e) => setRunId(Number(e.target.value))} style={selStyle}>
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  #{r.id} 第{r.stage_number}段 · {SCENARIO_LABEL[r.scenario]} · {VERDICT_LABEL[r.verdict]}
                </option>
              ))}
            </select>
          )}
          {runId && <button onClick={() => removeRun(runId)} style={{ ...selStyle, color: TH.critical, cursor: 'pointer' }}>删除</button>}
        </div>
      </div>

      {/* ── 状态条 ── */}
      {data && (
        <div style={{ display: 'flex', gap: 1, background: TH.statBorder }}>
          {[
            { l: '场景', v: SCENARIO_LABEL[data.run.scenario], c: TH.text },
            { l: '总判定', v: VERDICT_LABEL[data.run.verdict], c: VERDICT_COLOR[data.run.verdict] },
            { l: '峰值压力', v: `${data.run.peak_pressure_mpa} MPa`, c: TH.text },
            { l: '平均排量', v: `${data.run.avg_rate_m3min} m³/min`, c: TH.text },
            { l: '最大砂比', v: `${data.run.max_conc_kgm3} kg/m³`, c: TH.text },
            { l: '事件数', v: data.events.length, c: data.events.length ? TH.critical : TH.ok },
            { l: '时长', v: `${Math.round(data.run.duration_s / 60)} min`, c: TH.text },
            { l: '段号', v: `第 ${data.run.stage_number} 段`, c: TH.text },
          ].map((s, i) => (
            <div key={i} style={{ flex: 1, padding: '6px 8px', textAlign: 'center', background: '#fff' }}>
              <div style={{ fontSize: 9, color: TH.textMuted }}>{s.l}</div>
              <div style={{ fontSize: 14, fontWeight: 700, fontFamily: TH.fontMono, color: s.c }}>{s.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── 主体: 上 = 实时曲线 + 双对数, 下 = 事件流 ── */}
      {!data ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, color: TH.textSec }}>
          {loading ? null : runs.length === 0 ? (
            <>
              <div>本项目还没有任何监测数据</div>
              <div style={{ fontSize: 13 }}>选好方案/段/场景, 点 "⚡ 生成监测数据"</div>
            </>
          ) : (
            <div>请在右上角选择一条监测会话查看</div>
          )}
        </div>
      ) : (
        <div style={{ flex: 1, display: 'grid', gridTemplateRows: '1fr 240px', minHeight: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: TH.statBorder, minHeight: 0 }}>
            <Panel title="实时施工曲线 (压力 / 排量 / 砂比)">
              <RealtimeCurves data={data} />
            </Panel>
            <Panel title="Nolte-Smith 双对数曲线 — log(净压) vs log(时间)">
              <NolteSmithChart data={data} />
            </Panel>
          </div>
          <Panel title={`预警事件流 (${data.events.length})`}>
            <EventList data={data} />
          </Panel>
        </div>
      )}
    </div>
  );
}

const selStyle: React.CSSProperties = {
  fontSize: 12, padding: '4px 8px', borderRadius: 4,
  border: `1px solid ${TH.headerBorder}`, background: '#fff', color: TH.text,
};

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: TH.canvasBg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: TH.accent, background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}` }}>
        {title}
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────
// 实时曲线: 左 Y 轴 = 压力 (MPa), 右 Y 轴 = 排量 (m³/min) + 砂比 (kg/m³)
// ─────────────────────────────────────────────────
function RealtimeCurves({ data }: { data: MonitorRunData }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);

    const s = data.samples;
    if (!s.length) return;
    const padL = 50, padR = 50, padT = 18, padB = 32;
    const plotW = W - padL - padR, plotH = H - padT - padB;

    const tMin = 0, tMax = s[s.length - 1].t_s;
    const pMax = Math.max(...s.map((p) => p.surface_pressure_mpa)) * 1.1;
    const pMin = 0;
    const rcMax = Math.max(
      ...s.map((p) => p.rate_m3min),
      ...s.map((p) => p.proppant_conc_kgm3 / 20),
    ) * 1.1;

    const xAt = (t: number) => padL + ((t - tMin) / (tMax - tMin)) * plotW;
    const yAtP = (v: number) => padT + (1 - (v - pMin) / (pMax - pMin)) * plotH;
    const yAtR = (v: number) => padT + (1 - v / rcMax) * plotH;

    // 网格
    ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5;
    ctx.fillStyle = TH.textMuted; ctx.font = `9px ${TH.fontMono}`;
    for (let i = 0; i <= 5; i++) {
      const v = pMin + (pMax - pMin) * (1 - i / 5);
      const y = padT + (i / 5) * plotH;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      ctx.fillStyle = TH.pressure;
      ctx.fillText(v.toFixed(0), 6, y + 3);
      ctx.fillStyle = TH.rate;
      ctx.fillText((rcMax * (1 - i / 5)).toFixed(1), W - padR + 4, y + 3);
    }
    const minPerStep = (tMax / 60) > 30 ? 5 : (tMax / 60) > 12 ? 2 : 1;
    for (let m = 0; m <= tMax / 60; m += minPerStep) {
      const x = xAt(m * 60);
      ctx.strokeStyle = TH.grid; ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, H - padB); ctx.stroke();
      ctx.fillStyle = TH.textMuted; ctx.fillText(`${m}m`, x - 6, H - padB + 12);
    }

    // 轴标
    ctx.save(); ctx.translate(12, padT + plotH / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = TH.pressure; ctx.font = `bold 10px ${TH.fontUI}`;
    ctx.fillText('压力 (MPa)', -28, 0); ctx.restore();
    ctx.save(); ctx.translate(W - 14, padT + plotH / 2); ctx.rotate(Math.PI / 2);
    ctx.fillStyle = TH.rate; ctx.fillText('排量 / 砂比×0.05', -36, 0); ctx.restore();

    // 排量曲线
    ctx.strokeStyle = TH.rate; ctx.lineWidth = 1.2; ctx.beginPath();
    s.forEach((p, i) => {
      const x = xAt(p.t_s), y = yAtR(p.rate_m3min);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();

    // 砂比 (缩 1/20 共用右轴)
    ctx.strokeStyle = TH.conc; ctx.lineWidth = 1.2; ctx.beginPath();
    s.forEach((p, i) => {
      const x = xAt(p.t_s), y = yAtR(p.proppant_conc_kgm3 / 20);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();

    // 油压
    ctx.strokeStyle = TH.pressure; ctx.lineWidth = 1.5; ctx.beginPath();
    s.forEach((p, i) => {
      const x = xAt(p.t_s), y = yAtP(p.surface_pressure_mpa);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();

    // 净压 (虚线, 主诊断量)
    ctx.strokeStyle = TH.net; ctx.lineWidth = 1.2; ctx.setLineDash([4, 3]); ctx.beginPath();
    s.forEach((p, i) => {
      const x = xAt(p.t_s), y = yAtP(p.net_pressure_mpa);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.setLineDash([]);

    // 事件竖线
    data.events.forEach((e) => {
      const x = xAt(e.t_s);
      ctx.strokeStyle = e.level === 'critical' ? TH.critical : TH.warn;
      ctx.lineWidth = 1.2; ctx.setLineDash([2, 2]);
      ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, H - padB); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = e.level === 'critical' ? TH.critical : TH.warn;
      ctx.font = `bold 9px ${TH.fontUI}`;
      ctx.fillText(e.mode, x + 3, padT + 10);
    });

    // 图例
    const lg: [string, string][] = [
      ['油压', TH.pressure], ['净压', TH.net], ['排量', TH.rate], ['砂比×0.05', TH.conc],
    ];
    ctx.font = `9px ${TH.fontUI}`;
    let lx = padL + 4;
    lg.forEach(([lbl, c]) => {
      ctx.fillStyle = c; ctx.fillRect(lx, padT - 12, 10, 2);
      ctx.fillStyle = TH.textSec; ctx.fillText(lbl, lx + 13, padT - 9);
      lx += 56;
    });
  }, [data]);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

// ─────────────────────────────────────────────────
// Nolte-Smith 双对数 log(pn) vs log(t)
//   背景给出 5 个模式区(I/II/IIIa/IV/V) 参考斜率扇形
//   实测曲线按当前滑窗斜率上色
// ─────────────────────────────────────────────────
function NolteSmithChart({ data }: { data: MonitorRunData }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const lastSlope = data.analysis.slope.length
    ? data.analysis.slope[data.analysis.slope.length - 1]
    : 0;
  const lastMode = data.analysis.mode.length
    ? data.analysis.mode[data.analysis.mode.length - 1]
    : 'I';

  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);

    const s = data.samples.filter((p) => p.t_s > 1 && p.net_pressure_mpa > 0.3);
    if (s.length < 5) return;
    const padL = 50, padR = 12, padT = 18, padB = 30;
    const plotW = W - padL - padR, plotH = H - padT - padB;

    const logT = s.map((p) => Math.log10(p.t_s));
    const logP = s.map((p) => Math.log10(p.net_pressure_mpa));
    const xMin = Math.min(...logT), xMax = Math.max(...logT);
    const yMin = Math.min(...logP) - 0.05, yMax = Math.max(...logP) + 0.1;

    const xAt = (lt: number) => padL + ((lt - xMin) / (xMax - xMin)) * plotW;
    const yAt = (lp: number) => padT + (1 - (lp - yMin) / (yMax - yMin)) * plotH;

    // 背景: 模式参考斜率从最后一个点延伸
    const lastX = logT[logT.length - 1];
    const lastY = logP[logP.length - 1];
    const drawRefSlope = (k: number, color: string, label: string) => {
      const dx = xMax - xMin;
      const y2 = lastY + k * (xMax - lastX);
      const y0 = lastY + k * (xMin - lastX);
      ctx.strokeStyle = color + '55'; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(xAt(xMin), yAt(y0));
      ctx.lineTo(xAt(xMax), yAt(y2));
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = color; ctx.font = `9px ${TH.fontMono}`;
      ctx.fillText(label, xAt(xMax) - 60, yAt(y2) - 2);
    };
    drawRefSlope(0.25, MODE_COLOR.I, 'I  k=¼');
    drawRefSlope(0.0, MODE_COLOR.II, 'II  k≈0');
    drawRefSlope(1.0, MODE_COLOR.IIIa, 'IIIa  k=1');
    drawRefSlope(2.0, MODE_COLOR.IV, 'IV  k≥2');
    drawRefSlope(-0.5, MODE_COLOR.V, 'V  k<0');

    // 网格 + 刻度
    ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5;
    ctx.fillStyle = TH.textMuted; ctx.font = `9px ${TH.fontMono}`;
    for (let i = 0; i <= 5; i++) {
      const x = padL + (i / 5) * plotW;
      ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, H - padB); ctx.stroke();
      const lt = xMin + (i / 5) * (xMax - xMin);
      ctx.fillText((Math.pow(10, lt)).toFixed(0) + 's', x - 12, H - padB + 12);
    }
    for (let i = 0; i <= 5; i++) {
      const y = padT + (i / 5) * plotH;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      const lp = yMax - (i / 5) * (yMax - yMin);
      ctx.fillText((Math.pow(10, lp)).toFixed(1), 8, y + 3);
    }
    ctx.fillStyle = TH.textMuted; ctx.font = `10px ${TH.fontUI}`;
    ctx.fillText('log₁₀ (时间 / s)', padL + plotW / 2 - 30, H - 6);
    ctx.save(); ctx.translate(14, padT + plotH / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillText('log₁₀ (净压 / MPa)', -38, 0); ctx.restore();

    // 实测曲线: 按当前模式着色 (分段, 后端每点已分类)
    // 采样太密的话分段渲染开销小, 简化用整体一根, 颜色按当前最后模式
    const curMode = lastMode;
    ctx.strokeStyle = MODE_COLOR[curMode] || TH.text;
    ctx.lineWidth = 1.8; ctx.beginPath();
    for (let i = 0; i < s.length; i++) {
      const x = xAt(logT[i]), y = yAt(logP[i]);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();

    // 末点高亮
    ctx.fillStyle = MODE_COLOR[curMode] || TH.text;
    ctx.beginPath(); ctx.arc(xAt(lastX), yAt(lastY), 4, 0, Math.PI * 2); ctx.fill();

    // 事件标记
    data.events.forEach((e) => {
      const lt = Math.log10(Math.max(1, e.t_s));
      const idx = s.findIndex((p) => p.t_s >= e.t_s);
      const lp = idx >= 0 ? logP[idx] : 0;
      const x = xAt(lt), y = yAt(lp);
      ctx.strokeStyle = e.level === 'critical' ? TH.critical : TH.warn;
      ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.stroke();
    });

    // 右上角当前斜率徽章
    ctx.fillStyle = MODE_COLOR[curMode] || TH.text;
    ctx.font = `bold 11px ${TH.fontUI}`;
    ctx.fillText(`当前斜率 k = ${lastSlope.toFixed(2)}`, W - padR - 130, padT + 14);
    ctx.fillText(`模式: ${MODE_LABEL[curMode] || curMode}`, W - padR - 130, padT + 28);
  }, [data, lastSlope, lastMode]);

  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

// ─────────────────────────────────────────────────
// 事件流
// ─────────────────────────────────────────────────
function EventList({ data }: { data: MonitorRunData }) {
  const events = data.events;
  if (!events.length) {
    return (
      <div style={{ padding: 16, color: TH.textMuted, fontSize: 13, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        ✓ 全程未触发预警 — 工况判定为「{VERDICT_LABEL[data.run.verdict]}」
      </div>
    );
  }
  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '6px 0' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
        <thead>
          <tr style={{ background: TH.panelHeaderBg, position: 'sticky', top: 0 }}>
            {['时刻', '等级', '模式', '斜率', '事件描述', '处置建议'].map((h) => (
              <th key={h} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec, fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} style={{ borderBottom: '1px solid #F0EDE4' }}>
              <td style={{ padding: '6px 10px', fontFamily: TH.fontMono, whiteSpace: 'nowrap' }}>
                {Math.floor(e.t_s / 60)}m{Math.round(e.t_s % 60)}s
              </td>
              <td style={{ padding: '6px 10px' }}>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', fontSize: 11, borderRadius: 10,
                  background: e.level === 'critical' ? TH.critical : TH.warn, color: '#fff', fontWeight: 600,
                }}>{e.level === 'critical' ? '严重' : '警告'}</span>
              </td>
              <td style={{ padding: '6px 10px' }}>
                <span style={{ color: MODE_COLOR[e.mode] || TH.text, fontWeight: 700 }}>{e.mode}</span>
                <span style={{ color: TH.textMuted, marginLeft: 6 }}>{MODE_LABEL[e.mode]?.slice(MODE_LABEL[e.mode].indexOf(' ') + 1)}</span>
              </td>
              <td style={{ padding: '6px 10px', fontFamily: TH.fontMono }}>{e.slope.toFixed(2)}</td>
              <td style={{ padding: '6px 10px', color: TH.text }}>{e.message}</td>
              <td style={{ padding: '6px 10px', color: TH.textSec }}>{e.suggestion}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
